// Terrain (trees, mountains, lakes, ravines) is not stored anywhere: the game generates it from value-noise FBM with
// seed = world id and the parameters in village/get's map_features (world-process Utils/FeatureGenerator.cs). This is that
// code, step for step, in float32 (Math.fround) so a cell comes out the same here as on the server.
import type { MapFeature } from "@/api/types";

const f = Math.fround;

/** uint32 arithmetic: (seed * 2654435761) ^ (ix * 1619) ^ (iy * 31337), then two xorshift-multiply rounds. */
function hash2(ix: number, iy: number, seed: number): number {
  let h = (Math.imul(seed | 0, 2654435761 | 0) ^ Math.imul(ix | 0, 1619) ^ Math.imul(iy | 0, 31337)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return f(f(h) / f(4294967295)); // h / (float)uint.MaxValue — both sides float32, as in C#
}

const smoothstep = (t: number) => f(f(f(t) * f(t)) * f(3 - f(2 * t)));

function valueNoise(x: number, y: number, seed: number, scale: number): number {
  const gx = Math.floor(f(x / scale));
  const gy = Math.floor(f(y / scale));
  const tx = smoothstep(f(f(x - f(gx * scale)) / scale));
  const ty = smoothstep(f(f(y - f(gy * scale)) / scale));
  const v00 = hash2(gx, gy, seed);
  const v10 = hash2(gx + 1, gy, seed);
  const v01 = hash2(gx, gy + 1, seed);
  const v11 = hash2(gx + 1, gy + 1, seed);
  const a = f(f(v00 * f(1 - tx)) * f(1 - ty));
  const b = f(f(v10 * tx) * f(1 - ty));
  const c = f(f(v01 * f(1 - tx)) * ty);
  const d = f(f(v11 * tx) * ty);
  return f(f(f(a + b) + c) + d);
}

export function fbm(x: number, y: number, seed: number, baseScale: number, octaves: number, lacunarity: number, persistence: number): number {
  let value = f(0);
  let amp = f(1);
  let freq = f(1);
  let maxAmp = f(0);
  for (let o = 0; o < octaves; o++) {
    const s = (seed ^ Math.imul(o, 0x9e3779b9)) | 0;
    value = f(value + f(valueNoise(f(x * freq), f(y * freq), s, baseScale) * amp));
    maxAmp = f(maxAmp + amp);
    amp = f(amp * persistence);
    freq = f(freq * lacunarity);
  }
  return f(value / maxAmp);
}

export function isFeature(x: number, y: number, seed: number, mf: MapFeature): boolean {
  if (!mf || !mf.enabled) return false;
  const v = fbm(x, y, seed, f(mf.baseScale), mf.octaves | 0, f(mf.lacunarity), f(mf.persistence));
  return v >= f(mf.thresholdLo) && v <= f(mf.thresholdHi);
}

export type Terrain = "plain" | "trees" | "mountains" | "lakes" | "ravines";

/** What covers a cell, in the game's precedence (lakes and mountains and ravines block cities; trees are decoration). */
export function terrainAt(x: number, y: number, seed: number, mf: { trees: MapFeature; mountains: MapFeature; lakes: MapFeature; ravines: MapFeature }): Terrain {
  if (isFeature(x, y, seed, mf.lakes)) return "lakes";
  if (isFeature(x, y, seed, mf.mountains)) return "mountains";
  if (isFeature(x, y, seed, mf.ravines)) return "ravines";
  if (isFeature(x, y, seed, mf.trees)) return "trees";
  return "plain";
}

export const TERRAIN_CODE: Record<Terrain, number> = { plain: 0, trees: 1, mountains: 2, lakes: 3, ravines: 4 };
export const TERRAIN_OF_CODE: Terrain[] = ["plain", "trees", "mountains", "lakes", "ravines"];

/** Terrain codes for whole chunks, computed once each (a 64-cell chunk is ~310k hash calls, a few ms). */
export class TerrainCache {
  private chunks = new Map<string, Uint8Array>();
  constructor(
    public readonly seed: number,
    public readonly size: number,
    public readonly mf: { trees: MapFeature; mountains: MapFeature; lakes: MapFeature; ravines: MapFeature },
    public readonly chunk = 64,
  ) {}

  private build(cx: number, cy: number): Uint8Array {
    const c = this.chunk;
    const out = new Uint8Array(c * c);
    for (let ty = 0; ty < c; ty++) {
      for (let tx = 0; tx < c; tx++) {
        out[ty * c + tx] = TERRAIN_CODE[terrainAt(cx * c + tx, cy * c + ty, this.seed, this.mf)];
      }
    }
    return out;
  }

  /** The terrain code of a cell (coordinates wrap). */
  at(x: number, y: number): number {
    const size = this.size;
    const wx = ((x % size) + size) % size;
    const wy = ((y % size) + size) % size;
    const c = this.chunk;
    const cx = Math.floor(wx / c);
    const cy = Math.floor(wy / c);
    const key = `${cx},${cy}`;
    let ch = this.chunks.get(key);
    if (!ch) {
      ch = this.build(cx, cy);
      this.chunks.set(key, ch);
    }
    return ch[(wy - cy * c) * c + (wx - cx * c)];
  }

  isTrees(x: number, y: number): boolean {
    return this.at(x, y) === 1;
  }

  /** One cell's code without building its whole block (the far-out map samples a few cells per patch): the cached block's
   *  answer when it exists, else the game's noise for that cell alone. Same result as at(). */
  sample(x: number, y: number): number {
    const size = this.size;
    const wx = ((x % size) + size) % size;
    const wy = ((y % size) + size) % size;
    const c = this.chunk;
    const cx = Math.floor(wx / c);
    const cy = Math.floor(wy / c);
    const ch = this.chunks.get(`${cx},${cy}`);
    if (ch) return ch[(wy - cy * c) * c + (wx - cx * c)];
    return TERRAIN_CODE[terrainAt(wx, wy, this.seed, this.mf)];
  }
}
